import React from 'react';
import { Award, Users, Clock, ThumbsUp } from 'lucide-react';
import { companyInfo } from '../data/mock';

const AboutSection = () => {
  const stats = [
    { icon: Clock, value: '20+', label: 'Years in Business' },
    { icon: Users, value: '5000+', label: 'Happy Customers' },
    { icon: Award, value: '100%', label: 'Satisfaction Rate' },
    { icon: ThumbsUp, value: '4.9', label: 'Star Rating' }
  ];

  return (
    <section id="about" className="py-24 bg-[#FDFCF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Family-Owned Business Section */}
        <div className="mb-20 relative rounded-3xl overflow-hidden shadow-2xl">
          {/* Scenic gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#87CEEB] via-[#98D8C8] to-[#7AB8D8]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#622B62]/40 to-transparent" />
          
          <div className="relative z-10 grid lg:grid-cols-2 items-end">
            {/* Image - Branded Van */}
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_romano-modern/artifacts/2tociklr_Branded%20van.jpg"
                alt="Romano Heating and Cooling Service Van"
                className="w-full h-auto object-cover"
              />
            </div>
            
            {/* Content overlay */}
            <div className="p-8 lg:p-12 flex flex-col justify-center bg-gradient-to-l from-[#622B62]/95 to-[#622B62]/80 lg:bg-transparent">
              <div className="lg:bg-white/95 lg:backdrop-blur-sm lg:rounded-2xl lg:p-8 lg:shadow-xl">
                <span className="romano-badge mb-4 inline-block">About Us</span>
                <h3 className="romano-heading text-2xl sm:text-3xl font-bold text-white lg:text-[#622B62] mb-4">
                  Family-Owned & Operated
                </h3>
                <p className="text-white/90 lg:text-gray-600 leading-relaxed mb-4">
                  At Romano Heating and Cooling, we take pride in being a local, family-owned business. 
                  Our commitment to quality service and customer satisfaction has been the cornerstone 
                  of our success since 2005.
                </p>
                <p className="text-white/80 lg:text-gray-500 text-sm">
                  Licensed & Insured • LIC #1077499
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image Grid */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                {/* Branded Van Photo */}
                <div className="img-container rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-[#622B62] to-[#4A1F4A] p-1">
                  <img
                    src="https://customer-assets.emergentagent.com/job_romano-modern/artifacts/2tociklr_Branded%20van.jpg"
                    alt="Romano Heating and Cooling Service Van"
                    className="w-full h-64 object-cover rounded-xl"
                  />
                </div>
                {/* New Lennox Package */}
                <div className="img-container rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-[#DA9C3A] to-[#E8B55A] p-1">
                  <img
                    src="https://customer-assets.emergentagent.com/job_romano-modern/artifacts/njjfxv4s_New%20Lennox%20package.jpg"
                    alt="New Lennox package unit installation"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="img-container rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_romano-modern/artifacts/kimba918_Sutter%20st%20new%20coil%20%281%29.jpg"
                    alt="New condenser coil installation"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="img-container rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_romano-modern/artifacts/ojnw8x9v_New%20const.jpg"
                    alt="New construction HVAC ductwork"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-2xl p-6 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#622B62] flex items-center justify-center">
                <Award size={28} className="text-[#DA9C3A]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#622B62]">Since 2005</p>
                <p className="text-gray-500">Trusted Service</p>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div>
            <span className="romano-badge mb-4 inline-block">About Us</span>
            <h2 className="romano-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#622B62] mb-6">
              Your Trusted HVAC Partner Since 2005
            </h2>
            <div className="section-divider mb-6" />
            
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Founded in 2005, Romano Heating and Cooling has been dedicated to providing high-quality heating and cooling services to our community for over 18 years. As a locally owned business, we have an experienced team of technicians committed to keeping your home comfortable year-round.
            </p>
            
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              We specialize in a comprehensive range of residential HVAC services, including installation, maintenance, and repair. Whether you need air conditioning installation, furnace repair or routine maintenance, we have the expertise to meet your needs.
            </p>

            {/* Service Areas */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-8">
              <h3 className="font-bold text-[#622B62] mb-4">Proudly Serving</h3>
              <div className="flex flex-wrap gap-2">
                {companyInfo.serviceAreas.map((area) => (
                  <span
                    key={area}
                    className="px-4 py-2 bg-[#A7D7ED]/30 text-[#622B62] rounded-full text-sm font-medium"
                  >
                    {area}, CA
                  </span>
                ))}
              </div>
            </div>

            {/* Guarantee */}
            <div className="flex items-start gap-4 p-5 bg-[#DA9C3A]/10 rounded-xl border-l-4 border-[#DA9C3A]">
              <ThumbsUp size={24} className="text-[#DA9C3A] flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-[#622B62] mb-1">100% Satisfaction Guarantee</h4>
                <p className="text-gray-600">
                  We stand behind our work. If you're not completely satisfied,
                  we'll make it right.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 rounded-full bg-[#622B62] flex items-center justify-center mx-auto mb-4">
                <stat.icon size={24} className="text-[#DA9C3A]" />
              </div>
              <p className="text-3xl font-bold text-[#622B62] mb-1">{stat.value}</p>
              <p className="text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
